import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Button, Divider, message, UploadFile } from 'antd';
import { convertImgInFile } from '@utils/formatters';
import api from '@services/api';
import { BannerData } from '@services/types/General/banners';
import moment, { Moment } from 'moment';
import BannerExtraDetails from './BannerModalStepsContent/BannerExtraDetails';
import BannerConfig from './BannerModalStepsContent/BannerConfig';

interface BannerModalProps {
  visible: boolean;
  onClose: Function;
  bannerToEdit: BannerData | undefined;
  mutate: Function;
  isEdition: boolean;
}

const BannerModal: React.FC<BannerModalProps> = ({
  visible,
  onClose,
  bannerToEdit,
  mutate,
  isEdition,
}) => {
  const { t } = useTranslation('banners');
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [defaultFile, setDefaultFile] = useState<UploadFile<any>>();
  const [defaultFileDetail, setDefaultFileDetail] = useState<UploadFile<any>>();

  useEffect(() => {
    if (bannerToEdit?.fileName) {
      convertImgInFile(
        `/banners/${bannerToEdit?.fileName}`,
        bannerToEdit?.fileName,
        setDefaultFile,
      );
    }
  }, [bannerToEdit?.fileName]);

  useEffect(() => {
    if (bannerToEdit?.fileDetailName) {
      convertImgInFile(
        `/banners/${bannerToEdit?.fileDetailName}`,
        bannerToEdit?.fileDetailName,
        setDefaultFileDetail,
      );
    }
  }, [bannerToEdit?.fileDetailName]);

  const treatDate = useCallback((date: string) => {
    if (!date) {
      return undefined;
    }

    const currentDate = moment();
    const dateToTreat = moment(date);

    return dateToTreat.isBefore(currentDate) ? undefined : dateToTreat;
  }, []);

  const bannerToEditTreated = useMemo(() => {
    if (!isEdition || !bannerToEdit) {
      return undefined;
    }
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fileName,
      fileDetailName,
      link,
      startDate,
      expirationDate,
      customers,
      bannerDisplayIntentEnum,
      bannerDisplayProductEnum,
      buttonText,
      ...values
    } = bannerToEdit;

    const isTypeLink = !!link && link !== '';
    const isTypeImg = !!fileDetailName;
    const addInfoEnabled = isTypeLink || isTypeImg;

    return {
      ...values,
      link,
      addInfoEnabled,
      buttonText,
      startDate: treatDate(startDate),
      expirationDate: treatDate(expirationDate),
      file: defaultFile,
      fileDetail: defaultFileDetail,
      detailsType: addInfoEnabled && isTypeLink ? 'url' : 'img',
      recipientType: customers?.length > 0 ? 'customer' : 'type',
      type: bannerDisplayIntentEnum,
      product: bannerDisplayProductEnum,
      enabledDefaultButton: !!buttonText,
      customers: customers?.map(cst => cst.id),
    };
  }, [bannerToEdit, defaultFile, defaultFileDetail, isEdition, treatDate]);

  useEffect(() => {
    form?.setFieldsValue(bannerToEditTreated);
  }, [form, bannerToEditTreated]);

  const createBanner = useCallback(
    (files: FormData, params: any) =>
      api.post('/banner', files, {
        params,
      }),
    [],
  );

  const editBanner = useCallback(
    (files: FormData, params: any) =>
      api.put('/banner', files, {
        params,
      }),
    [],
  );

  const getFile = useCallback(
    (name: string | undefined) =>
      name && {
        uid: -1,
        name,
        status: 'done',
        url: `/banners/${name}`,
      },
    [],
  );

  const defaultMainFile = useMemo(
    () => getFile(bannerToEdit?.fileName),
    [bannerToEdit?.fileName, getFile],
  );

  const defaultDetailFile = useMemo(
    () => getFile(bannerToEdit?.fileDetailName),
    [bannerToEdit?.fileDetailName, getFile],
  );

  const handleClose = useCallback(() => {
    onClose();
    form?.resetFields();
  }, [form, onClose]);

  const formatDate = useCallback(
    (date?: Moment) => date && date.format('YYYY-MM-DDTHH:mm:ss'),
    [],
  );

  const handleOnFinish = useCallback(async () => {
    setConfirmLoading(true);
    try {
      const {
        addInfoEnabled,
        file,
        fileDetail,
        type,
        product,
        startDate,
        expirationDate,
        customers,
        buttonText,
        enabledDefaultButton,
        ...values
      } = formValues;

      const payload = {
        ...values,
        startDate: formatDate(startDate ?? moment()),
        expirationDate: formatDate(expirationDate),
        displayIntentEnum: type,
        displayProductEnum: product,
        customers: customers ? customers?.join(',') : undefined,
        buttonText:
          enabledDefaultButton && !buttonText
            ? t('modal.button-default-text')
            : buttonText,
      };

      const files = new FormData();
      files.append('file', file);

      if (addInfoEnabled) {
        files.append('fileDetail', fileDetail);
      }

      if (isEdition) {
        await editBanner(files, {
          ...payload,
          bannerId: bannerToEditTreated?.id,
        }).then(({ data }) => mutate(data));
      } else {
        await createBanner(files, payload).then(({ data }) => mutate(data));
      }
      message.success(
        t(`msgs.success-when-${isEdition ? 'editing' : 'creating'}`),
      );
      handleClose();
    } catch {
      message.error(t(`msgs.error-when-${isEdition ? 'editing' : 'creating'}`));
    }
    setConfirmLoading(false);
  }, [
    formValues,
    formatDate,
    t,
    isEdition,
    handleClose,
    editBanner,
    bannerToEditTreated?.id,
    mutate,
    createBanner,
  ]);

  const footer = useMemo(
    () => (
      <div style={{ textAlign: 'right', marginTop: 40 }}>
        <Divider />
        <Button type="primary" htmlType="submit" loading={confirmLoading}>
          {t('buttons.confirm')}
        </Button>
      </div>
    ),
    [t, confirmLoading],
  );

  const handleOnFinishFailed = useCallback(() => {
    const modalWrapEl = document.querySelector('.ant-modal-wrap');
    modalWrapEl?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Modal
      title={t(`modal.title-${isEdition ? 'edit' : 'new'}`)}
      open={visible}
      onCancel={handleClose}
      bodyStyle={{ padding: 0 }}
      footer={false}
    >
      <Form
        form={form}
        style={{ padding: '32px 40px' }}
        layout="vertical"
        onFinish={handleOnFinish}
        onFinishFailed={handleOnFinishFailed}
      >
        {/* @ts-ignore */}
        <BannerConfig defaultFile={defaultMainFile} />
        {formValues?.addInfoEnabled && (
          <BannerExtraDetails
            form={form}
            // @ts-ignore
            defaultFile={defaultDetailFile}
          />
        )}
        {footer}
      </Form>
    </Modal>
  );
};

export default BannerModal;
